import { uniqBy } from "lodash";
import * as React from "react";
import { Link } from "react-router-dom";
import { MepAnalysisSet, WebPortalClient } from "webportalclient";
import { DateMatchSortHeader } from "../../../Components/DateMatchSortHeader";
import { MatchSortHeader } from "../../../Components/MatchSortHeader";
import { OverreadStatusHeader } from "../../../Components/OverreadStatusHeader";
import { getAnalysisSetsQcBlindingFilter, useQuery } from "../../../Components/Query";
import { Column, Table } from "../../../Components/QueryTable";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import { useReferenceContextProvider } from "../../../Core/ReferenceContextProvider";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import { PressureAnalysisSetTypeEnum } from "../../../misc/api/ApiPressureAnalysisSetTypeEnum";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { OverreadStatusIdEnum, Permission, User } from "../../../misc/api/apiTypes";
import { AnalysisAcceptabilityStatus } from "../../../misc/components/analysis/AnalysisAcceptabilityStatus";
import { AssignToUserInGroupModal } from "../../../misc/components/AssignToUserInGroupModal";
import { Authorization } from "../../../misc/components/Authorization";
import Condition from "../../../misc/components/Condition";
import { formatDateTime } from "../../../misc/components/Formatters/Formatters";
import { HamburgerMenu } from "../../../misc/components/HamburgerMenu";
import { CreateAnalysisBatchModal } from "../../QC/CreateAnalysisBatchModal";
import { AnalysisSetsTableAutomationId, getAnalysisSetsTableAutomationId, getQueryTableAutomationId, QueryTableAutomationId } from "../../../misc/Helpers/AutomationIdHelper";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { SessionChallengeStatus } from "../../../misc/components/SessionChallengeStatus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { createFormatters } from "../../Shared/AnalysisHelpers";

export const MepAnalysisListTable = () => {
    const { data, applyFilters, refresh } = useQuery();
    const { study, fieldDisplayText } = useCurrentStudyConfigurationContext();
    const { overreadStatuses } = useReferenceContextProvider();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const client = useWebPortalApiClient();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [analystSetTypes, setAnalystSetTypes] = React.useState([]);
    const [analysts, setAnalysts] = React.useState([]);
    const [qcBatchSaved, setQcBatchSaved] = React.useState<boolean | undefined>(undefined);
    const [qcSelectionModalVisible, setQCSelectionModelVisible] = React.useState(false);
    const [sessionIdToReassign, setSessionIdToReassign] = React.useState(null);
    const [isReassignToUserModalVisible, setReassignToUserModalVisible] = React.useState(false);
    const [isAssignToUserModalVisible, setAssignToUserModalVisible] = React.useState(false);
    const [sessionIdToSendForQC, setSessionIdToSendForQC] = React.useState(null);
    const [overreadChallengeStatuses, setOverreadChallengeStatuses] = React.useState([]);
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {

            const visits = await client.client.getMepSessions("visit", `visit/subject/site/studyId eq ${study.id}`, "visit/name", "visit($select=name)");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getMepSessions("name", `visit/subject/site/studyId eq ${study.id}`, "name");
            const options = uniqBy(sessions.value, o => o.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            setSessionOptions(options);
        };

        const getAnalystSetTypes = async () => {
            const types = await legacyClient.getMepAnalysisSetTypes();
            const options = uniqBy(types, o => o.Id).map(s => ({
                value: s.Id,
                label: s.Name,
            }));

            setAnalystSetTypes(options);
        };

        const getAnalysts = async () => {

            const analysts = await client.client.getMepAnalysisSets("analyst,analystId", `session/visit/subject/site/studyId eq ${study.id}`, "analyst");

            const options = uniqBy(analysts.value, o => o.analystId).filter(o => o.analystId && o.analyst).map(s => ({
                value: s.analystId,
                label: s.analyst,
            }));

            setAnalysts(options);
        };

        const getOverreadChallengeStatuses = async () => {
            const overreadChallengeStatuses = await client.client.getOverreadChallengeStatuses();

            const options = uniqBy(overreadChallengeStatuses.value, o => o.name).filter(o => o.name).map(s => ({
                value: s.id,
                label: s.name,
            }));

            setOverreadChallengeStatuses(options);
        };

        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getAnalystSetTypes();
        getAnalysts();
        getOverreadChallengeStatuses();
    }, [study.id]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }))

    const isQcAnalysisSet = (set: MepAnalysisSet): boolean => set.type === PressureAnalysisSetTypeEnum.QC;

    const formatters = createFormatters<MepAnalysisSet>(isQcAnalysisSet);
    const formatAnalyst = analyst => analyst ? analyst : "-";

    const formatStatus = (analysisSet: MepAnalysisSet) => {
        return (<>
            <AnalysisAcceptabilityStatus value={analysisSet.status} /><br />
            <SessionChallengeStatus challengeIsClosed={analysisSet.session.challengeIsClosed} challengeStatus={analysisSet.session.overreadChallengeStatus} analysisOverreadStatus={analysisSet.status} />
        </>);
    }

    const formatAnalystSetType = (type: string) => {
        switch (type) {
            case PressureAnalysisSetTypeEnum.Overread:
                return "Overread";
            case PressureAnalysisSetTypeEnum.Site:
                return "Site";
            case PressureAnalysisSetTypeEnum.QC:
                return "QC";
        }
    }

    const handleQcBatchSavingAlertMessage = () => {
        if (qcBatchSaved) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-success">
                        <FontAwesomeIcon icon={faCheck} className="icon" />
                        QC batch is created successfully.
                    </div>
                </div>
            </div>);
        }
        else if (qcBatchSaved === false) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-danger">
                        <FontAwesomeIcon icon={faXmark} className="icon" />
                        There was an error. QC batch wasn't saved.
                    </div>
                </div>
            </div>);
        }
        else {
            return null
        }
    }

    const handleReassign = async (sessionId: string, userId: string) => {
        await legacyClient.reassignMep(sessionId, userId);
        setReassignToUserModalVisible(false);
        refresh();
    }

    const handleSendForQC = async (sessionId: string, userId: string) => {
        await legacyClient.createMepQC(sessionId, PressureAnalysisSetTypeEnum.QC, userId);
        setAssignToUserModalVisible(false);
        refresh();
    }

    const ViewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    const RenderHamburgerMenu = (analysisSet: MepAnalysisSet, currentPermissions: Permission[], currentStudyId: string) => {
        return (
            <HamburgerMenu contextMenuLeftAligned={true} automationId={`${getQueryTableAutomationId(QueryTableAutomationId.Options)}_${analysisSet.id}`}>
                <Authorization permissionId={PermissionEnum.AccessSingleMepSession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => (analysisSet.type !== PressureAnalysisSetTypeEnum.QC) || (analysisSet.type === PressureAnalysisSetTypeEnum.QC && analysisSet.analystId === currentUser.Id)}>
                        <li>
                            <Link to={`/Mep/Analysis/${analysisSet.id}/view`} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.ViewAnalysis)}_${analysisSet.id}`}>View</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadMepSession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet !== null && analysisSet.type !== PressureAnalysisSetTypeEnum.Site && (!analysisSet.analystId || analysisSet.analystId === currentUser.Id)}>
                        <li>
                            <Link to={`/Mep/Analysis/${analysisSet.id}/overread`} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.OverreadAnalysis)}_${analysisSet.id}`}>Over-read</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.CreateQcMepAnalysisSet}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === PressureAnalysisSetTypeEnum.Overread}>
                        <li>
                            <a onClick={() => { setAssignToUserModalVisible(true); setSessionIdToSendForQC(analysisSet.sessionId); }} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.QcReview)}_${analysisSet.id}`}>
                                Manually Send for QC Review...
                            </a>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadMepSession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === PressureAnalysisSetTypeEnum.Overread && (analysisSet.status === OverreadStatusIdEnum.Pending || analysisSet.status === OverreadStatusIdEnum.NotStarted)}>
                        <li>
                            <a onClick={() => { setSessionIdToReassign(analysisSet.id); setReassignToUserModalVisible(true); }}>
                                Reassign
                            </a>
                        </li>
                    </Condition>
                </Authorization>
            </HamburgerMenu>
        );
    };

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">MEP Analysis Sets</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button className="btn btn-primary" onClick={clearAllFilters} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.ClearAllFilters)}`}>
                            Clear All Filters
                        </button>
                        &nbsp;
                    </div>
                    <div className="h2 pull-right">
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.CreateAutomatedQcMepSelection}>
                            <button className="btn btn-primary" data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.CreateQcBatch)}`} onClick={() => setQCSelectionModelVisible(true)}>Create QC Batch</button>
                            &nbsp;
                        </Authorization>
                    </div>
                </div>
            </div>
            {handleQcBatchSavingAlertMessage()}
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div className="table-responsive" style={{ minHeight: "900px" }}>
                                <Table data={data} automationId="MepAnalysisTable">
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SiteNumber)}`} header={<MatchSortHeader title="Site ID" field="session/visit/subject/site/id" options={siteOptions} sortField="session/visit/subject/site/number" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SiteNumber)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => formatters.formatSiteId(set)} checkBlinded={true} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectNumber)}`} header={<MatchSortHeader title={fieldDisplayText.SubjectId} field="session/visit/subject/id" options={subjectOptions} sortField="session/visit/subject/number" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectNumber)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => formatters.formatSubjectId(set)} checkBlinded={true} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.VisitName)}`} header={<MatchSortHeader title="Visit" field="session/visit/name" options={visitOptions} sortField="session/visit/name" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.VisitName)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => formatters.formatVisitLabel(set)} checkBlinded={true} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SessionLabel)}`} header={<MatchSortHeader title="Session" field="session/name" options={sessionOptions} sortField="session/name" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SessionLabel)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => formatters.formatSessionLabel(set)} checkBlinded={true} />
                                    <Column field="type" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.AnalysisSetType)}`} header={<MatchSortHeader title="Analysis Set Type" field="type" options={analystSetTypes} automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.AnalysisSetType)}`} />} formatter={type => formatAnalystSetType(type)} />
                                    <Column field="created" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.CreatedAt)}`} header={<DateMatchSortHeader title="Created At" field="created" sortField="created" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.CreatedAt)}`} />} formatter={created => formatDateTime(created)} />
                                    <Column field="started" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.StartedAt)}`} header={<DateMatchSortHeader title="Started At" field="started" sortField="started" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.StartedAt)}`} />} formatter={started => formatDateTime(started)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Status)}`} header={<OverreadStatusHeader challengeField="session/overreadChallengeStatus" useStatusField={true} overreadAnalysisSetTypeValue={PressureAnalysisSetTypeEnum.Overread} automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Status)}`} orderField="status" overreadStatuses={statusesOptions} overreadChallengeStatuses={overreadChallengeStatuses} />} formatter={analysisSet => formatStatus(analysisSet)} />
                                    {ViewOverreaderName()
                                        ? <Column automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Overreader)}`} field="analyst" header={<MatchSortHeader title="Over-reader" field="analystId" options={analysts} sortField="analyst" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Overreader)}`} />} formatter={analyst => formatAnalyst(analyst)} />
                                        : null
                                    }
                                    <Column field="" formatter={analysisSet => RenderHamburgerMenu(analysisSet, currentUserPermissions, study.id)} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {qcSelectionModalVisible ? <CreateAnalysisBatchModal currentUser={currentUser} sessionType={SessionTypes.Mep} minimumTests={3} onSave={(result: boolean) => setQcBatchSaved(result)} onClose={() => setQCSelectionModelVisible(false)} /> : null}

            <AssignToUserInGroupModal show={isAssignToUserModalVisible} headerText="Create QC Analysis Set &amp; assign to user" permission={PermissionEnum.OverreadMepSession}
                onClose={() => setAssignToUserModalVisible(false)}
                onAssign={(user) => handleSendForQC(sessionIdToSendForQC, user)} />

            <AssignToUserInGroupModal show={isReassignToUserModalVisible} headerText="Reassign Analysis Set" permission={PermissionEnum.OverreadMepSession}
                onClose={() => setReassignToUserModalVisible(false)}
                onAssign={(user) => { handleReassign(sessionIdToReassign, user) }} />
        </>
    );
}
